import is from '@sindresorhus/is';
import clsx from 'clsx';
import { Fragment, type JSX, type ReactNode } from 'react';

import { Button } from '../../../core/button/Button';
import { IconArrowDown, IconArrowUp, IconCrossSmall, IconExpand45, IconMinimize45 } from '../../../core/icons';
import type { Language } from '../../language-picker/LanguagePicker';
import { LanguagePicker } from '../../language-picker/LanguagePicker';

export const HeaderContainer: React.FunctionComponent<{
    readonly children: ReactNode;
    readonly className?: string;
}> = ({ children, className }) => (
    <div
        className={clsx(
            'flex items-center box-border flex-row shrink-0 justify-between p-0 relative w-full z-[1] font-sans',
            className,
        )}
    >
        {children}
    </div>
);

export const NavigationContainer: React.FunctionComponent<{
    readonly children: ReactNode;
    readonly className?: string;
}> = ({ children, className }) => (
    <div className={clsx('flex items-center self-stretch flex-row', className)}>{children}</div>
);

export const NavigationButtons: React.FunctionComponent<{
    readonly children: ReactNode;
    readonly className?: string;
}> = ({ children, className }) => (
    <div
        className={clsx(
            'flex items-center box-border flex-row shrink-0 flex-wrap h-full justify-start p-0 relative',
            className,
        )}
    >
        {children}
    </div>
);

export const ActionButtons: React.FunctionComponent<{
    readonly children: ReactNode;
    readonly className?: string;
}> = ({ children, className }) => (
    <div className={clsx('flex items-center box-border flex-row shrink-0 gap-m justify-start p-0 relative', className)}>
        {children}
    </div>
);

export const Seperator: React.FunctionComponent<{ readonly className?: string }> = ({ className }) => (
    <div className={clsx('bg-gray-300 h-[1rem] mx-xxl w-px', className)} />
);

type SidepanelHeaderProps<T extends string> = {
    readonly actions?: ReactNode;
    readonly activeLanguage: string | undefined;
    readonly disableArrows?: boolean;
    readonly isExpanded?: boolean;
    readonly isGenerating: boolean;
    readonly isLanguagePickerDisabled: boolean;
    readonly languages: readonly Language<T>[];
    readonly onClose: () => void;
    readonly onNext?: () => void;
    readonly onPrevious?: () => void;
    readonly renderLanguageIndicator?: (language: Language<T>) => ReactNode;
    readonly setActiveLanguage: (value: T) => void;
};

export const SidepanelHeader = <T extends string>({
    actions,
    activeLanguage,
    disableArrows = false,
    isExpanded = false,
    isGenerating,
    isLanguagePickerDisabled,
    languages,
    onClose,
    onNext,
    onPrevious,
    setActiveLanguage,
}: SidepanelHeaderProps<T>): JSX.Element => {
    return (
        <HeaderContainer>
            <NavigationContainer>
                <NavigationButtons>
                    <div className="flex items-center box-border flex-row shrink-0 gap-m justify-start p-0 relative">
                        <Button aria-label="close" iconOnly={true} size="sm" variant="secondary" onClick={onClose}>
                            <IconCrossSmall />
                        </Button>
                        <Button disabled={true} iconOnly={true} size="sm" variant="secondary">
                            {isExpanded ? <IconMinimize45 /> : <IconExpand45 />}
                        </Button>
                    </div>
                    <Seperator />
                    {!disableArrows && (
                        <Fragment>
                            <div className="flex items-center box-border flex-row shrink-0 gap-m justify-start p-0 relative">
                                <Button
                                    aria-label="previous"
                                    disabled={is.undefined(onPrevious)}
                                    iconOnly={true}
                                    size="sm"
                                    variant="secondary"
                                    onClick={onPrevious}
                                >
                                    <IconArrowUp />
                                </Button>
                                <Button
                                    aria-label="next"
                                    disabled={is.undefined(onNext)}
                                    iconOnly={true}
                                    size="sm"
                                    variant="secondary"
                                    onClick={onNext}
                                >
                                    <IconArrowDown />
                                </Button>
                            </div>
                            <Seperator />
                        </Fragment>
                    )}
                    <LanguagePicker
                        activeText={activeLanguage}
                        disabled={isLanguagePickerDisabled}
                        isGenerating={isGenerating}
                        languages={languages}
                        onLanguageChange={setActiveLanguage}
                    />
                </NavigationButtons>
            </NavigationContainer>
            <ActionButtons>{actions}</ActionButtons>
        </HeaderContainer>
    );
};
